/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */
import { ColorObject, decomposeColor } from '@material-ui/core';
import { EventLogRecord } from '@moderepo/mode-apis';
import { selectTimeSeriesCollectionData, userAppDataContext, fetchTimeSeriesCollectionData, selectEventLogs, UIFetchEventLogFilter, fetchEventLogs,
    selectHomeVideos, UIFetchHomeVideosFilters, fetchHomeVideos } from '@moderepo/mode-data-state-user-app';
import { Entity, EntityBlobDefinition, EntityMetricsDefinition, EntityMetricsDefinitionLinkTo, SortOrder, TimeSeriesAggregation, VideoInfo }
    from '@moderepo/mode-internal-apis';
import moment from 'moment';
import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { EntityTripsRouteMapUIComponentCustomSettings } from '.';
import { FetchMissingEventLogDataParams, isValueToEventStateMapping, ValueToEventStateMapping } from '../..';
import { getMetricsDefinitionMetricsId, getMultipleEntities } from '../../..';
import { isObjectLiteral, isValidSettingStringValue, sortObjectAttributes } from '../../../../../utils/utils';
import { useSelectEntity } from '../../../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../../../utils/logger';


export const MOBILITY_DEFAULT_VEHICLE_ENTITY_CLASS = 'vehicle';

// Default metricsDefinitionIds of the metrics definition where the trip info, locations, and events are stored.
export const MOBILITY_DEFAULT_TRIP_EVENT_METRICS_DEFINITION_ID = 'trip_event';
export const MOBILITY_DEFAULT_VEHICLE_ASSIGNMENT_EVENT_METRICS_DEFINITION_ID = 'vehicle_assignment_event';
export const MOBILITY_DEFAULT_LOCATION_METRICS_DEFINITION_ID = 'location';
export const MOBILITY_DEFAULT_EVENT_METRICS_DEFINITION_ID = 'vehicle_event';

// Default metric name used for trip info
export const MOBILITY_DEFAULT_TRIP_INFO_START_TIME_METRIC_NAME = 'start_time';
export const MOBILITY_DEFAULT_TRIP_INFO_END_TIME_METRIC_NAME = 'end_time';
export const MOBILITY_DEFAULT_TRIP_INFO_DURATION_METRIC_NAME = 'duration';
export const MOBILITY_DEFAULT_TRIP_INFO_DRIVER_ENTITY_ID_METRIC_NAME = 'driver_entity_id';
export const MOBILITY_DEFAULT_TRIP_INFO_VEHICLE_ENTITY_ID_METRIC_NAME = 'vehicle_entity_id';

// Default metric name used for trip location data
export const MOBILITY_DEFAULT_LOCATION_LATITUDE_METRIC_NAME = 'lat';
export const MOBILITY_DEFAULT_LOCATION_LONGITUDE_METRIC_NAME = 'lon';
export const MOBILITY_DEFAULT_LOCATION_SPEED_METRIC_NAME = 'speed';
export const MOBILITY_DEFAULT_LOCATION_HEADING_METRIC_NAME = 'heading';
export const MOBILITY_DEFAULT_LOCATION_DURATION_METRIC_NAME = 'duration';
export const MOBILITY_DEFAULT_LOCATION_DISTANCE_METRIC_NAME = 'distance';

// Default metric name used for trip events
export const MOBILITY_DEFAULT_EVENT_KEY_METRIC_NAME = 'event_key';
export const MOBILITY_DEFAULT_EVENT_TYPE_METRIC_NAME = 'event_type';
export const MOBILITY_DEFAULT_EVENT_LATITUDE_METRIC_NAME = 'lat';
export const MOBILITY_DEFAULT_EVENT_LONGITUDE_METRIC_NAME = 'lon';
export const MOBILITY_DEFAULT_EVENT_SPEED_METRIC_NAME = 'speed';

export const MOBILITY_DEFAULT_LOW_SPEED_COLOR = 'rgb(0, 200, 255)';
export const MOBILITY_DEFAULT_HIGH_SPEED_COLOR = 'rgb(0, 0, 255)';
export const MOBILITY_DEFAULT_ROUTE_LINE_THICKNESS = 8;
export const MOBILITY_DEFAULT_VEHICLE_COLOR = 'rgb(255, 0, 0)';

export const MOBILITY_VEHICLE_ANIMATION_DURATION = 1000;


// The maximum number of searchKeys allowed by back end when making API call to fetch videos
const MAX_SEARCH_KEYS = 20;

// Fetching Events and Videos data require fetching page by page and we need to pass the limit to the backend.
const FETCH_TRIPS_INFO_PAGE_LIMIT = 1000;
const FETCH_EVENTS_PAGE_LIMIT = 100;
const FETCH_VIDEOS_PAGE_LIMIT = 100;
const MAX_PAGES = 10;



export enum EventMarkerIcon {
    INFO = 'info',
    WARNING = 'warning',
}


/**
 * The vehicle location data at 1 specific timestamp. This interface maps to the data from the location metrics definition.
 */
export interface RouteLocation {
    readonly timestamp: number;
    readonly lat: number;
    readonly lon: number;
    readonly speed: number | undefined;
    readonly heading: number | undefined;
    readonly duration: number | undefined;
    readonly distance: number | undefined;
}


/**
 * The interface for 1 vehicle event. This interface maps to the data from the vehicle_event log record
 */
export interface RouteVehicleEvent {
    readonly timestamp: number;
    readonly eventKey: string;
    readonly eventType: string;
    readonly lat: number;
    readonly lon: number;
    readonly speed: number;

    readonly fields: EventLogRecord['fields'];
    readonly videos?: readonly VideoInfo[] | undefined;
}


/**
 * The interface for 1 segment, a line between 2 route location. A route consists of multiple locations and the UI will draw a line between
 * each pair of location, a segment. Since we want to color each segment based on the speed of one of the locations, we will need to a data
 * structure to store the info for each pair of locations and the color.
 */
export interface RouteSegment {
    readonly startLocation: RouteLocation;
    readonly endLocation: RouteLocation;
    readonly color: string;
}



/**
 * The information for a Trip. NOTE that this DOES NOT contain that actual trip data. This interface contains only the information the component
 * needed to be able to fetch data
 */
export interface TripInfo {
    readonly vehicleEntityId: string;
    readonly driverEntityId: string;

    // The start and end time of the trip
    readonly startTime: number;
    readonly endTime: number;
    readonly duration: number;    // Duration is in seconds
}


/**
 * This interface for a data object that contains all the data for 1 trip
 */
export interface TripData {
    // A reference to the tripInfo object
    readonly tripInfo: TripInfo;

    readonly vehicle: Entity;
    readonly driver?: Entity | undefined;

    // location data loaded from back end
    readonly locations: readonly RouteLocation[];

    // The segments of the routes which is only available if the "showVehicleSpeed" is true. The data comes from "locations"
    readonly segments?: readonly RouteSegment[] | undefined;

    readonly events?: RouteVehicleEvent[] | undefined;
}


/**
 * The name of the Trip event metrics that the project uses. For trip event metrics, the component needs to get the start_time, end_time, and
 * duration values from the trip event metrics data. However, the component does not know what the name of these metrics that each project uses.
 * Different project can use different name. Therefore, we need a mapping where the admin can tell the component what the metric name that
 * project uses. The component has the default names for these metrics. This config is only needed when a project uses
 * different names for it's event metrics.
 */
export interface TripInfoMetricsNameMapping {
    readonly startTime?: string | undefined;
    readonly endTime?: string | undefined;
    readonly duration?: string | undefined;
    readonly driverEntityId?: string | undefined;
    readonly vehicleEntityId?: string | undefined;
}


/**
 * The name of the LOCATION metrics that the project uses. For location metrics, the component needs to get the lat, lon, speed, heading,
 * etc... values from the location metrics data. However, the component does not know what the name of these metrics that each project uses.
 * Different project can use different name. Therefore, we need a mapping where the admin can tell the component what the metric name that
 * project uses. The component has the default names for these metrics. This config is only needed when a project uses
 * different names for it's event metrics.
 */
export interface LocationMetricsNameMapping {
    readonly lat?: string | undefined;
    readonly lon?: string | undefined;
    readonly speed?: string | undefined;
    readonly heading?: string | undefined;
    readonly duration?: string | undefined;
    readonly distance?: string | undefined;
}


/**
 * Similar to the LocationMetricsNameMapping except this is used for the Event metrics
 */
export interface EventMetricsNameMapping {
    readonly eventKey?: string | undefined;
    readonly eventType?: string | undefined;
    readonly lat?: string | undefined;
    readonly lon?: string | undefined;
    readonly speed?: string | undefined;
}



/**
 * The settings required for fetching trip info data. Most of the attributes are optional because there is already default settings for these
 * attributes. Only projects that have different settings need to provide the settings.
 */
export interface TripInfoDataSettings {
    readonly metricsDefinitionId?: string | undefined;
    readonly vehicleAssignmentEventMetricsDefinitionId?: string | undefined;

    // The name of the trip info metrics. The component has the default names for these metrics. This config is only needed when a project uses
    // different names for it's trip info metrics.
    readonly metricsNameMapping?: TripInfoMetricsNameMapping | undefined;
}

/**
 * type guard function to check if an object is a type of TripInfoDataSettings
 */
export const isTripInfoDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TripInfoDataSettings => {
    const settings = obj as TripInfoDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" must be undefined or a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.vehicleAssignmentEventMetricsDefinitionId)) {
        return errorLogger?.logError({
            message: '"vehicleAssignmentEventMetricsDefinitionId" must be undefined or a non-empty string.',
            value  : settings.vehicleAssignmentEventMetricsDefinitionId,
            path   : [...configPath, 'vehicleAssignmentEventMetricsDefinitionId'],
        }) ?? false;
    }

    if (settings.metricsNameMapping !== undefined) {
        if (!isObjectLiteral(settings.metricsNameMapping)) {
            return errorLogger?.logError({
                message: '"metricsNameMapping" must be undefined or an Object.',
                value  : settings.metricsNameMapping,
                path   : [...configPath, 'metricsNameMapping'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.startTime)) {
            return errorLogger?.logError({
                message: '"startTime" must be undefined or non-empty string.',
                value  : settings.metricsNameMapping.startTime,
                path   : [...configPath, 'metricsNameMapping', 'startTime'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.endTime)) {
            return errorLogger?.logError({
                message: '"endTime" must be undefined or non-empty string.',
                value  : settings.metricsNameMapping.endTime,
                path   : [...configPath, 'metricsNameMapping', 'endTime'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.duration)) {
            return errorLogger?.logError({
                message: '"duration" must be undefined or non-empty string.',
                value  : settings.metricsNameMapping.duration,
                path   : [...configPath, 'metricsNameMapping', 'duration'],
            }) ?? false;
        }
    }

    return true;
};



/**
 * The settings required for fetching trip location data. Most of the attributes are optional because there is already default settings for these
 * attributes. Only projects that have different settings need to provide the settings.
 */
export interface TripLocationDataSettings {
    // The ID of the metrics definition where route location data are stored. By default, the the component will use the metrics definition
    // with ID "location". If a project uses a different metrics definition, the admin can use this to specify the ID of metrics definition
    // that the project uses.
    readonly metricsDefinitionId?: string | undefined;

    // The name of the location metrics. The component has the default names for these metrics. This config is only needed when a project uses
    // different names for it's location metrics.
    readonly metricsNameMapping?: LocationMetricsNameMapping | undefined;
}

/**
 * type guard function to check if an object is a type of TripLocationDataSettings
 */
export const isTripLocationDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TripLocationDataSettings => {
    const settings = obj as TripLocationDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" must be undefined or a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    if (settings.metricsNameMapping !== undefined) {
        if (!isObjectLiteral(settings.metricsNameMapping)) {
            return errorLogger?.logError({
                message: '"metricsNameMapping" must be undefined or an Object.',
                value  : settings.metricsNameMapping,
                path   : [...configPath, 'metricsNameMapping'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.lat)) {
            return errorLogger?.logError({
                message: '"lat" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.lat,
                path   : [...configPath, 'metricsNameMapping', 'lat'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.lon)) {
            return errorLogger?.logError({
                message: '"lon" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.lon,
                path   : [...configPath, 'metricsNameMapping', 'lon'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.speed)) {
            return errorLogger?.logError({
                message: '"speed" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.speed,
                path   : [...configPath, 'metricsNameMapping', 'speed'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.heading)) {
            return errorLogger?.logError({
                message: '"heading" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.heading,
                path   : [...configPath, 'metricsNameMapping', 'heading'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.distance)) {
            return errorLogger?.logError({
                message: '"distance" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.distance,
                path   : [...configPath, 'metricsNameMapping', 'distance'],
            }) ?? false;
        }
    }

    return true;
};



/**
 * The settings required for fetching trip events data. Most of the attributes are optional because there is already default settings for these
 * attributes. Only projects that have different settings need to provide the settings.
 */
export interface TripEventDataSettings {
    // The ID of the metrics definition where route events data are stored. By default, the the component will use the metrics definition
    // with ID "vehicle_event". If a project uses a different metrics definition, the admin can use this to specify the ID of metrics definition
    // that the project uses.
    readonly metricsDefinitionId?: string | undefined;

    // The name of the event metrics. The component has the default names for these metrics. This config is only needed when a project uses
    // different names for it's event metrics.
    readonly metricsNameMapping?: EventMetricsNameMapping | undefined;

    /**
     * Each eventType has special meaning. Their value can be used to determine the event's state, bad, good, etc... This component
     * does not know what the values means therefore, it is up to the admin to configure them. An event can have 4 different states (5 if
     * we include none as another state). The admin need to tell the component which value mean which state e.g.
     *
     *      valueToEventStateMapping: {
     *          "2001": "info",          // For this metric, value "2001" means the event is just information
     *          "2002": "good"           // value "2002" means the event is good
     *          "2003": "serious",       // and value "2003" means the entity is in a bad state but not the worst
     *          "2004": "critical",      // and value "2004" means the entity is in the worst state
     *          "2005": "critical",      // and "2005" is also the worst state
     *      }
     *
     * The component will highlight the marker based on this event's state mapping. If this is NOT configured, the component will treat all
     * eventType is SERIOUS
     */
    readonly eventTypeToEventStateMapping?: ValueToEventStateMapping | undefined;
}


/**
 * type guard function to check if an object is a type of TripEventDataSettings
 */
export const isTripEventDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TripEventDataSettings => {
    const settings = obj as TripEventDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" must be undefined or a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    if (settings.metricsNameMapping !== undefined) {
        if (!isObjectLiteral(settings.metricsNameMapping)) {
            return errorLogger?.logError({
                message: '"metricsNameMapping" must be undefined or an Object.',
                value  : settings.metricsNameMapping,
                path   : [...configPath, 'metricsNameMapping'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.eventKey)) {
            return errorLogger?.logError({
                message: '"eventKey" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.eventKey,
                path   : [...configPath, 'metricsNameMapping', 'eventKey'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.eventType)) {
            return errorLogger?.logError({
                message: '"eventType" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.eventType,
                path   : [...configPath, 'metricsNameMapping', 'eventType'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.lat)) {
            return errorLogger?.logError({
                message: '"lat" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.lat,
                path   : [...configPath, 'metricsNameMapping', 'lat'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.lon)) {
            return errorLogger?.logError({
                message: '"lon" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.lon,
                path   : [...configPath, 'metricsNameMapping', 'lon'],
            }) ?? false;
        }
        if (!isValidSettingStringValue(settings.metricsNameMapping.speed)) {
            return errorLogger?.logError({
                message: '"speed" must be undefined or a non-empty string.',
                value  : settings.metricsNameMapping.speed,
                path   : [...configPath, 'metricsNameMapping', 'speed'],
            }) ?? false;
        }
    }

    if (
        settings.eventTypeToEventStateMapping
        && !isValueToEventStateMapping(settings.eventTypeToEventStateMapping, errorLogger, [...configPath, 'eventTypeToEventStateMapping'])
    ) {
        return false;
    }

    return true;
};



/**
 * Convert 1 event log record fetched from HOME_APP_PROXY to TripInfo object.
 * E.g.
 *      Given:
 *          // The event log raw data
 *          eventLogRecord: {
 *              timestamp: "2022-10-19T00:56:57Z",
 *              fields: {
 *                  start_time: "2022-10-01T23:13:17Z",
 *                  end_time: "2022-10-02T05:52:18Z",
 *                  duration: 23941,
 *              }
 *          }
 *
 *      Return:
 *          {
 *              startTime: "2022-10-01T23:13:17Z",
 *              endTime: "2022-10-02T05:52:18Z",
 *              duration: 23941,
 *              vehicleEntityId: "[VEHICLE_ID]"
 * ,            driverEntityId: "[DRIVER_ID]"
 *          }
 */
const eventLogRecordToTripInfo = (
    eventLog: EventLogRecord,
    metricsMapping: {
        readonly startTime: string;
        readonly endTime: string;
        readonly duration: string;
        readonly driverEntityId: string;
        readonly vehicleEntityId: string;
    },
): TripInfo | undefined => {

    const startTime = typeof eventLog.fields[metricsMapping.startTime] === 'string' ? moment(eventLog.fields[metricsMapping.startTime]).valueOf() : undefined;
    const endTime = typeof eventLog.fields[metricsMapping.endTime] === 'string' ? moment(eventLog.fields[metricsMapping.endTime]).valueOf() : undefined;
    const duration = typeof eventLog.fields[metricsMapping.duration] === 'number' ? eventLog.fields[metricsMapping.duration] : undefined;
    const driverEntityId = typeof eventLog.fields[metricsMapping.driverEntityId] === 'string' ? eventLog.fields[metricsMapping.driverEntityId] : undefined;
    const vehicleEntityId = typeof eventLog.fields[metricsMapping.vehicleEntityId] === 'string' ? eventLog.fields[metricsMapping.vehicleEntityId] : undefined;


    if (startTime === undefined || endTime === undefined || !duration || !vehicleEntityId || !driverEntityId) {
        return undefined;
    }

    return {
        startTime,
        endTime,
        duration,
        vehicleEntityId,
        driverEntityId,
    };
};



/**
 * Convert 1 row of RAW location data from TSDB collection to RouteLocation object.
 * E.g.
 *      Given:
 *          // The row of raw data
 *          row: ["2022-10-11T23:00:00Z", 9.782978723404, 35.68775255319, 139.784084893, 120.31700382978, 6.382978723404, 0.01770212765957]
 *
 *          // The column # where each metric is stored within the "row" array
 *          columns: {lat:1, lon:2, speed:3, heading: 4, duration:5, distance:6}
 *      To:
 *          {
 *              timestamp: "2022-10-11T23:00:00Z",
 *              speed: 9.782978723404,
 *              lat: 35.68775255319,
 *              lon: 139.784084893,
 *              heading: 120.31700382978,
 *              duration: 6.382978723404,
 *              distance: 0.01770212765957
 *          }
 */
const collectionDataRowToRouteLocation = (
    row: readonly [string, ...(string | number | null)[]],
    metricsMapping: {
        readonly lat: {
            readonly column: number | undefined;
        },
        readonly lon: {
            readonly column: number | undefined;
        },
        readonly speed: {
            readonly column: number | undefined;
        },
        readonly heading: {
            readonly column: number | undefined;
        },
        readonly duration: {
            readonly column: number | undefined;
        },
        readonly distance: {
            readonly column: number | undefined;
        }
    },
): RouteLocation | undefined => {

    // the first column is the date
    const timestamp = moment(row[0]).valueOf();

    // All the location values based on column number for each metric
    const lat = metricsMapping.lat.column !== undefined && typeof row[metricsMapping.lat.column] === 'number' ? Number(row[metricsMapping.lat.column]) : undefined;
    const lon = metricsMapping.lon.column !== undefined && typeof row[metricsMapping.lon.column] === 'number' ? Number(row[metricsMapping.lon.column]) : undefined;
    const speed = metricsMapping.speed.column !== undefined && typeof row[metricsMapping.speed.column] === 'number' ? Number(row[metricsMapping.speed.column]) : undefined;
    const heading = metricsMapping.heading.column !== undefined && typeof row[metricsMapping.heading.column] === 'number' ? Number(row[metricsMapping.heading.column]) : undefined;
    const duration = metricsMapping.duration.column !== undefined && typeof row[metricsMapping.duration.column] === 'number' ? Number(row[metricsMapping.duration.column]) : undefined;
    const distance = metricsMapping.distance.column !== undefined && typeof row[metricsMapping.distance.column] === 'number' ? Number(row[metricsMapping.distance.column]) : undefined;

    // RoutLocation needs at least a lat and lon value. If these 2 values are undefined, return undefined.
    if (lat === undefined || lon === undefined) {
        return undefined;
    }

    return {
        timestamp,
        lat,
        lon,
        speed,
        heading,
        duration,
        distance,
    };
};



/**
 * Convert 1 event log record fetched from HOME_APP_PROXY to RouteVehicleEvent object.
 * E.g.
 *      Given:
 *          // The event log raw data
 *          eventLogRecord: {
 *              timestamp: "2022-10-19T00:56:57Z",
 *              fields: {
 *                  event_key: "2001",
 *                  event_type: "2001",
 *                  lat: 35.76399,
 *                  lon: 139.86584000000002,
 *                  speed: 14
 *              }
 *          }
 *
 *      Return:
 *          {
 *              timestamp: "2022-10-19T00:56:57Z",
 *              eventKey: "2001",
 *              eventType: "2001",
 *              speed: 14,
 *              lat: 35.76399,
 *              lon: 139.86584000000002,
 *          }
 */
const eventLogRecordToVehicleEvent = (
    eventLog: EventLogRecord,
    metricsMapping: {
        readonly eventKey: string;
        readonly eventType: string;
        readonly lat: string;
        readonly lon: string;
        readonly speed: string;
    },
): RouteVehicleEvent => {

    // the first column is the date
    const timestamp = moment(eventLog.timestamp).valueOf();

    const eventKey = typeof eventLog.fields[metricsMapping.eventKey] === 'string' ? eventLog.fields[metricsMapping.eventKey] : undefined;
    const eventType = typeof eventLog.fields[metricsMapping.eventType] === 'string' ? eventLog.fields[metricsMapping.eventType] : undefined;
    const lat = typeof eventLog.fields[metricsMapping.lat] === 'number' ? eventLog.fields[metricsMapping.lat] : 0;
    const lon = typeof eventLog.fields[metricsMapping.lon] === 'number' ? eventLog.fields[metricsMapping.lon] : 0;
    const speed = typeof eventLog.fields[metricsMapping.speed] === 'number' ? eventLog.fields[metricsMapping.speed] : undefined;

    return {
        timestamp,
        eventKey,
        eventType,
        lat,
        lon,
        speed,
        fields: eventLog.fields,
    };
};



/**
 * Given a vehicle current speed, return a color based on the speed's range.
 * For example: If minSpeed is 100mph and the color is blue green rgb(0, 100, 255) and maxSpeed is 150mph and the color is solid BLUE, rgb(0, 0, 255),
 * then the color of the current speed will be a color between rgb(0, 100, 255) and rgb(0, 0, 255). We will find the currentSpeed relative to
 * the speed's range. For example, if currentSpeed is 125mph then it is 50% relative to the speed's range. which mean its' color is half way between
 * the color for the min and max speed. Therefore, the color will be rgb(0, 50, 255). If currentSpeed is 120mph then it is 40% between the range
 * therefore its' color is rgba(0, 60, 255)
 */
const getColorByVehicleSpeed = (
    currentSpeed: number | undefined,
    minSpeed: number,
    maxSpeed: number,
    lowSpeedColor: ColorObject,
    hightSpeedColor: ColorObject,
): string => {

    const [red1, green1, blue1] = lowSpeedColor.values;
    const [red2, green2, blue2] = hightSpeedColor.values;

    const redDiff = red2 - red1;
    const greenDiff = green2 - green1;
    const blueDiff = blue2 - blue1;

    if (currentSpeed) {
        const range = maxSpeed - minSpeed;
        const currentSpeedRelative = ((currentSpeed - minSpeed) / range);

        const red = Math.floor(currentSpeedRelative * redDiff);
        const green = Math.floor(currentSpeedRelative * greenDiff);
        const blue = Math.floor(currentSpeedRelative * blueDiff);

        return `rgb(${red1 + red}, ${green1 + green}, ${blue1 + blue})`;
    }

    return `rgb(${red2}, ${green2}, ${blue2})`;
};



/**
 * Given an array of location objects, find and return the min/max range of the speed values in those location objects
 */
const getSpeedRange = (
    locations: readonly RouteLocation[],
    currentRange: {
        readonly min: number; readonly max: number;
    },
): {
    readonly min: number; readonly max: number;
} => {
    return locations.reduce((result, location) => {
        if (location.speed !== undefined) {
            return {
                min: Math.min(result.min, location.speed),
                max: Math.max(result.max, location.speed),
            };
        }

        return result;
    }, currentRange);
};


/**
 * Given an array of route locations, create array of segments for each pair of locations. For example
 *      Input:
 *          locations: [l1, l2, l3, l4, ...]
 *      Output:
 *          [{l1, l2}, {l2, l3}, {l3, l4}, ...]
 *
 */
const createSegments = (
    locations: readonly RouteLocation[],
    speedRange: {
        readonly min: number; readonly max: number;
    },
    lineColors: [string, ...string[]] | undefined,
): readonly RouteSegment[] => {

    // Get the low and high speed color from the lineColors array. Use the first color as the color for the low speed and second color for high
    // speed. If only 1 color is specified, use the same color for both low and high speed which mean the line will be the same color.
    // If none of the colors is provided, use the default color
    const lowSpeedColor = lineColors?.[0];
    const highSpeedColor = lineColors?.[1];
    const lowSpeedColorObject = decomposeColor(lowSpeedColor ?? MOBILITY_DEFAULT_LOW_SPEED_COLOR);
    const highSpeedColorObject = decomposeColor(highSpeedColor ?? lowSpeedColor ?? MOBILITY_DEFAULT_HIGH_SPEED_COLOR);

    const segments: RouteSegment[] = [];

    let startPosition: RouteLocation | undefined;
    locations.forEach((location) => {
        if (location.lon && location.lat) {
            const currentPosition = location;

            if (startPosition) {
                segments.push({
                    color        : getColorByVehicleSpeed(currentPosition.speed, speedRange.min, speedRange.max, lowSpeedColorObject, highSpeedColorObject),
                    startLocation: startPosition,
                    endLocation  : currentPosition,
                });
            }
            startPosition = currentPosition;
        }
    });

    return segments;
};


/**
 * Fetch Trips info
 */
export const useFetchTripsInfoData = (
    projectId: number,
    targetEntityId: string,
    startTime: number | undefined,
    endTime: number | undefined,
    sortOrder: SortOrder | undefined,
    tripEventMetricsDefinition: EntityMetricsDefinition | undefined,
    tripEventSearchKeyMetricsDefinition: EntityMetricsDefinition | undefined,
    tripsInfoDataSettings: TripInfoDataSettings | undefined,
): readonly TripInfo[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // Get all the Entity object for the target entity
    const targetEntity = useSelectEntity(projectId, targetEntityId);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly FetchMissingEventLogDataParams[] | undefined>();

    const hasErrors = useRef<boolean>(false);


    /**
     * Get the trips data from cache. If a trip data is not fetched, add the params needed to fetch for that trip to the
     * paramsForFetchingMissingData so that we can go through that list and fetch the missing data later
     */
    const tripsInfo = useMemo((): TripInfo[] | undefined => {


        if (!hasErrors.current && targetEntity && tripEventMetricsDefinition && tripEventSearchKeyMetricsDefinition && startTime !== undefined && endTime !== undefined) {

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: FetchMissingEventLogDataParams [] = [];

            /**
             * Because we don't want to assume all projects will have the same metrics definition for events data or assume the
             * metrics name are the same, we will need to make sure the metrics name are configurable.
             *
             * Create a temporary map of metric info use for fetching and parsing event log data later. We need the names of the metrics that the
             * project uses for event data e.g. start_time, end_time, duration, etc... If the project has custom names then use the name defined in
             * "tripsInfoDataSettings.metricsNameMapping". If not, use the default metric names.
             */
            const eventMetricsMapping = {
                startTime      : tripsInfoDataSettings?.metricsNameMapping?.startTime ?? MOBILITY_DEFAULT_TRIP_INFO_START_TIME_METRIC_NAME,
                endTime        : tripsInfoDataSettings?.metricsNameMapping?.endTime ?? MOBILITY_DEFAULT_TRIP_INFO_END_TIME_METRIC_NAME,
                duration       : tripsInfoDataSettings?.metricsNameMapping?.duration ?? MOBILITY_DEFAULT_TRIP_INFO_DURATION_METRIC_NAME,
                driverEntityId : tripsInfoDataSettings?.metricsNameMapping?.driverEntityId ?? MOBILITY_DEFAULT_TRIP_INFO_DRIVER_ENTITY_ID_METRIC_NAME,
                vehicleEntityId: tripsInfoDataSettings?.metricsNameMapping?.vehicleEntityId ?? MOBILITY_DEFAULT_TRIP_INFO_VEHICLE_ENTITY_ID_METRIC_NAME,
            };


            // All the TripInfos data we can get from cache will be stored here.
            const allTripsInfo: TripInfo[] = [];

            const searchKeys = getMetricsDefinitionMetricsId(targetEntity, tripEventSearchKeyMetricsDefinition.metricsDefinitionId);

            // If we fetch 100 records per page then we should expect this loop not to go more than 10 times. If there are more
            // than 10 pages then there must be something wrong and we should stop.
            let pageNumber = 0;
            while (pageNumber < MAX_PAGES) {
                const filters: UIFetchEventLogFilter = {
                    searchKeys,
                    begin   : moment(startTime).toISOString(),
                    end     : moment(endTime).toISOString(),
                    pageSize: FETCH_TRIPS_INFO_PAGE_LIMIT,
                    pageNumber,
                    timezone: targetEntity.timeZone,
                    sortBy  : sortOrder,
                };

                // Select the cached trip info data from the data state
                const cachedTripsInfo = selectEventLogs(
                    appDataState, targetEntity.homeId, tripEventMetricsDefinition.dataStoreConfiguration.moduleInstanceId, filters,
                );

                if (cachedTripsInfo) {
                    if (cachedTripsInfo.records && cachedTripsInfo.records.length > 0) {

                        // Convert each record from the eventData.records to TripInfo object and add them to the allTripsInfo
                        cachedTripsInfo.records.forEach((record) => {
                            const tripInfo = eventLogRecordToTripInfo(record, eventMetricsMapping);
                            if (tripInfo) {
                                allTripsInfo.push(tripInfo);
                            }
                        });
                    }

                    // If backend return no records or we already reached the end of the records then break
                    if (!cachedTripsInfo.records || cachedTripsInfo.records.length === 0 || (pageNumber + 1) * FETCH_TRIPS_INFO_PAGE_LIMIT >= cachedTripsInfo.total) {
                        break;
                    }
                } else {
                    // If we can't find the data from cache, it is probably not fetched. Add it to the list of data we need to fetch.
                    newParamsForFetchingMissingData.push({
                        homeId         : targetEntity.homeId,
                        logResourcePath: tripEventMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                        filters,
                    });
                    break;
                }
                pageNumber += 1;
            }



            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (
                    !currentParams
                  || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });

            // If we don't have any missing data to fetch then we can return the result
            if (newParamsForFetchingMissingData.length === 0) {
                return allTripsInfo;
            }
        }

        return undefined;
    }, [targetEntity, tripEventMetricsDefinition, tripEventSearchKeyMetricsDefinition, startTime, endTime,
        tripsInfoDataSettings?.metricsNameMapping?.startTime, tripsInfoDataSettings?.metricsNameMapping?.endTime,
        tripsInfoDataSettings?.metricsNameMapping?.duration, tripsInfoDataSettings?.metricsNameMapping?.driverEntityId,
        tripsInfoDataSettings?.metricsNameMapping?.vehicleEntityId, sortOrder, appDataState]);



    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchEventLogs(action.homeId, action.logResourcePath, action.filters)).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData]);


    return tripsInfo;
};



/**
 * Custom hook to fetch all the entities involved in an array of trips. Each trip has a vehicle and driver and this hook will go through all
 * the trip and compile the list of unique vehicles/driver and fetch all the entities object of those.
 */
export const useFetchTripsVehicleAndDriverEntities = (
    projectId: number,
    tripsInfo: readonly TripInfo[] | undefined,
): readonly Entity[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    const [entities, setEntities] = useState<readonly Entity[]>();

    // Compile the list of entity IDs that we need to fetch data for. This includes the vehicles and drivers in the TripInfo array
    const entityIds = useMemo(() => {
        if (tripsInfo) {
            // The array of trips can involve multiple vehicles therefore we need to fetch multiple entities. Also, the driver can switch
            // vehicles back and forth therefore it is possible the the trips contain duplicate vehicle so we need to make sure the array of
            // entityIds is unique
            const uniqueVehicleIds = new Set<string>();
            tripsInfo.forEach((info) => {
                uniqueVehicleIds.add(info.vehicleEntityId);
                if (info.driverEntityId) {
                    uniqueVehicleIds.add(info.driverEntityId);
                }
            });
            return Array.from(uniqueVehicleIds);
        }
        return undefined;
    }, [tripsInfo]);



    // Now fetch all the entities in the vehicleIds
    useEffect(() => {
        if (entityIds) {
            getMultipleEntities(projectId, entityIds, appDataState, appDataDispatch).then((result) => {
                setEntities(result);
            });
        }
    }, [appDataDispatch, appDataState, projectId, entityIds]);

    return entities;
};



// Temporary Interface for storing params required for fetching TSDB data
interface FetchTSDBDataParams {
    readonly homeId: number;
    readonly moduleId: string;
    readonly collectionId: string,
    readonly startTime: number;
    readonly endTime: number;
    readonly valueNames: string[];
}



/**
 * Fetch a vehicle's location data for multiple trips from TSDB
 * This will return an Array of RouteLocation[], one for each trip. So it will return a RouteLocation[][].
 * NOTE: that the result array can contain "undefined" if this function is unable to fetch locations for a trip. The caller must filter out
 * undefined element in the array if necessary.
 */
export const useFetchMultipleTripsLocationData = (
    projectId: number,
    homeId: number | undefined,
    locationMetricsDefinition: EntityMetricsDefinition | undefined,
    tripsInfo: readonly TripInfo[] | undefined,
    locationDataSettings: TripLocationDataSettings | undefined,
): readonly (RouteLocation[] | undefined)[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // Get all the entities involved in the trips
    const entities = useFetchTripsVehicleAndDriverEntities(projectId, tripsInfo);


    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly FetchTSDBDataParams[] | undefined>();
    const hasErrors = useRef<boolean>(false);


    /**
     * Get the trips data from cache. If a trip data is not fetched, add the params needed to fetch for that trip to the
     * paramsForFetchingMissingData so that we can go through that list and fetch the missing data later
     */
    const allTripsRouteLocations = useMemo((): readonly (RouteLocation[] | undefined)[] | undefined => {

        if (!hasErrors.current && homeId && tripsInfo && locationMetricsDefinition && entities) {

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: FetchTSDBDataParams [] = [];

            /**
             * Because we don't want to assume all projects will have the same metrics definition for trip location data or assume the
             * metrics name are the same, we will need to make sure the metrics name are configurable.
             *
             * Create a temporary map of metric info use for fetching and parsing TSDB data later. We need the names of the metrics that the
             * project uses for location data e.g. lat, lon, speed, etc... If the project has custom names then use the name defined in
             * "locationDataSettings.metricsNameMapping". If not, use the default metric names.
             * We also need the metrics' column. After we fetched collection data, which is a double array of values, we will need to get the
             * values from the array based based on the order of the metric names array we passed to the backend. For example, if we pass "lat"
             * as the first item in the array of metric names then when we parse the data, we will need get the latitude value from column 1.
             * NOTE: the reason the first column is column 1 is because we need to skip the first column which is used for the date.
             */
            const locationMetricsMapping = {
                lat: {
                    name  : locationDataSettings?.metricsNameMapping?.lat ?? MOBILITY_DEFAULT_LOCATION_LATITUDE_METRIC_NAME,
                    column: 1,
                },
                lon: {
                    name  : locationDataSettings?.metricsNameMapping?.lon ?? MOBILITY_DEFAULT_LOCATION_LONGITUDE_METRIC_NAME,
                    column: 2,
                },
                speed: {
                    name  : locationDataSettings?.metricsNameMapping?.speed ?? MOBILITY_DEFAULT_LOCATION_SPEED_METRIC_NAME,
                    column: 3,
                },
                heading: {
                    name  : locationDataSettings?.metricsNameMapping?.heading ?? MOBILITY_DEFAULT_LOCATION_HEADING_METRIC_NAME,
                    column: 4,
                },
                duration: {
                    name  : locationDataSettings?.metricsNameMapping?.duration ?? MOBILITY_DEFAULT_LOCATION_DURATION_METRIC_NAME,
                    column: 5,
                },
                distance: {
                    name  : locationDataSettings?.metricsNameMapping?.distance ?? MOBILITY_DEFAULT_LOCATION_DISTANCE_METRIC_NAME,
                    column: 6,
                },
            };


            // Get the metric names array from the locationMetricsInfo. We will need this array of metricNames to fetch TSDB data
            const metricNames = Object.values(locationMetricsMapping).map((metric) => {
                return metric.name;
            });


            // All the Trips' location data we can get from cache will be stored here.
            const allTripsRouteLocationsResult: (RouteLocation[] | undefined)[] = [];

            tripsInfo.forEach((tripInfo) => {

                // Find the Vehicle object associated with the trip. NOTE, it is possible that the vehicle is not found, when a vehicle entity
                // is deleted.
                const vehicle = entities.find((vehicle) => {
                    return vehicle.entityId === tripInfo.vehicleEntityId;
                });

                if (!vehicle) {
                    // We can't find the entity associated with the trip. Add "undefined" to the "tripsLocationData" array to make sure the
                    // tripsLocationData array matches the tripsInfo array. This way we can map trip location data to trip info just based on the
                    // array index.
                    allTripsRouteLocationsResult.push(undefined);
                    return;
                }

                const collectionId = getMetricsDefinitionMetricsId(vehicle, locationMetricsDefinition.metricsDefinitionId);

                // Select the cached vehicle's route data from the data state/cache
                const cachedLocationRawData = selectTimeSeriesCollectionData(
                    appDataState, homeId, locationMetricsDefinition.dataStoreConfiguration.moduleInstanceId, collectionId,
                    moment(tripInfo.startTime).toISOString(), moment(tripInfo.endTime).toISOString(),
                    metricNames, TimeSeriesAggregation.AVG,
                );

                if (cachedLocationRawData) {
                    // If the data for the trip exist in cache, convert it from collection 2D data table to array of RouteLocation
                    const oneTripRouteLocations: RouteLocation[] = [];
                    cachedLocationRawData.data.forEach((row) => {
                        const location = collectionDataRowToRouteLocation(row, locationMetricsMapping);
                        if (location) {
                            oneTripRouteLocations.push(location);
                        }
                    });

                    allTripsRouteLocationsResult.push(oneTripRouteLocations);
                } else {
                    // If we can't find the data from cache, it is probably not fetched. Add it to the list of data we need to fetch.
                    newParamsForFetchingMissingData.push({
                        homeId,
                        moduleId  : locationMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                        collectionId,
                        startTime : tripInfo.startTime,
                        endTime   : tripInfo.endTime,
                        valueNames: metricNames,
                    });
                }
            });


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (
                    !currentParams
                    || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });


            // if we don't have and missing data to be fetched then we can return the result
            if (newParamsForFetchingMissingData.length === 0) {
                return allTripsRouteLocationsResult;
            }
        }

        return undefined;

    }, [appDataState, entities, homeId, locationDataSettings?.metricsNameMapping?.distance, locationDataSettings?.metricsNameMapping?.duration,
        locationDataSettings?.metricsNameMapping?.heading, locationDataSettings?.metricsNameMapping?.lat,
        locationDataSettings?.metricsNameMapping?.lon, locationDataSettings?.metricsNameMapping?.speed, locationMetricsDefinition, tripsInfo]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            paramsForFetchingMissingData.forEach((params) => {
                appDataDispatch(fetchTimeSeriesCollectionData(
                    params.homeId,
                    params.moduleId,
                    params.collectionId,
                    moment(params.startTime).toISOString(),
                    moment(params.endTime).toISOString(),
                    params.valueNames,
                    TimeSeriesAggregation.AVG,
                )).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData]);


    return allTripsRouteLocations;
};



/**
 * Fetch event data for multiple trips
 */
export const useFetchMultipleTripsEventsData = (
    projectId: number,
    homeId: number | undefined,
    eventMetricsDefinition: EntityMetricsDefinition | undefined,
    tripsInfo: readonly TripInfo[] | undefined,
    eventDataSettings: TripEventDataSettings | undefined,
): readonly (RouteVehicleEvent[] | undefined)[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // Get all the entities involved in the trips
    const entities = useFetchTripsVehicleAndDriverEntities(projectId, tripsInfo);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly FetchMissingEventLogDataParams[] | undefined>();
    const hasErrors = useRef<boolean>(false);


    /**
     * Get the trips data from cache. If a trip data is not fetched, add the params needed to fetch for that trip to the
     * paramsForFetchingMissingData so that we can go through that list and fetch the missing data later
     */
    const allTripsEventLogRecords = useMemo((): (RouteVehicleEvent[] | undefined)[] | undefined => {


        if (!hasErrors.current && homeId && eventMetricsDefinition && tripsInfo && entities) {

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: FetchMissingEventLogDataParams [] = [];

            /**
             * Because we don't want to assume all projects will have the same metrics definition for events data or assume the
             * metrics name are the same, we will need to make sure the metrics name are configurable.
             *
             * Create a temporary map of metric info use for fetching and parsing event log data later. We need the names of the metrics that the
             * project uses for event data e.g. lat, lon, speed, etc... If the project has custom names then use the name defined in
             * "eventDataSettings.metricsNameMapping". If not, use the default metric names.
             */
            const eventMetricsMapping = {
                eventKey : eventDataSettings?.metricsNameMapping?.eventKey ?? MOBILITY_DEFAULT_EVENT_KEY_METRIC_NAME,
                eventType: eventDataSettings?.metricsNameMapping?.eventType ?? MOBILITY_DEFAULT_EVENT_TYPE_METRIC_NAME,
                lat      : eventDataSettings?.metricsNameMapping?.lat ?? MOBILITY_DEFAULT_EVENT_LATITUDE_METRIC_NAME,
                lon      : eventDataSettings?.metricsNameMapping?.lon ?? MOBILITY_DEFAULT_EVENT_LONGITUDE_METRIC_NAME,
                speed    : eventDataSettings?.metricsNameMapping?.speed ?? MOBILITY_DEFAULT_EVENT_SPEED_METRIC_NAME,
            };



            // All the Trips' event logs data we can get from cache will be stored here.
            const allTripsEventLogRecordsResult: (RouteVehicleEvent[] | undefined)[] = [];

            tripsInfo.forEach((tripInfo) => {

                // Find the Vehicle object associated with the trip. NOTE, it is possible that the vehicle is not found, when a vehicle entity
                // is deleted.
                const vehicle = entities.find((vehicle) => {
                    return vehicle.entityId === tripInfo.vehicleEntityId;
                });

                if (!vehicle) {
                    // We can't find the entity associated with the trip. Add "undefined" to the "tripsLogRecords" array to make sure the
                    // tripsLogRecords array matches the tripsInfo array. This way we can map trip event records data to trip info just based on the
                    // array index.
                    allTripsEventLogRecordsResult.push(undefined);
                    return;
                }

                const searchKeys = getMetricsDefinitionMetricsId(vehicle, eventMetricsDefinition.metricsDefinitionId);
                const oneTripsEventLogRecords: RouteVehicleEvent[] = [];

                // If we fetch 1000 records per page then we should expect this loop not to go more than 10 times. If there are more
                // than 10 pages then there must be something wrong and we should stop.
                let pageNumber = 0;
                while (pageNumber < MAX_PAGES) {
                    const filters: UIFetchEventLogFilter = {
                        searchKeys,
                        begin   : moment(tripInfo.startTime).toISOString(),
                        end     : moment(tripInfo.endTime).toISOString(),
                        pageSize: FETCH_EVENTS_PAGE_LIMIT,
                        pageNumber,
                    };

                    // Select the cached vehicle's event log data from the data state/cache
                    const cachedEventData = selectEventLogs(
                        appDataState, homeId, eventMetricsDefinition.dataStoreConfiguration.moduleInstanceId, filters,
                    );

                    if (cachedEventData) {
                        if (cachedEventData.records && cachedEventData.records.length > 0) {
                            // Convert each event record from the eventData.records to RouteVehicleEvent object and add them to the oneTripsEventLogRecords
                            oneTripsEventLogRecords.push(...(cachedEventData.records.map((record) => {
                                return eventLogRecordToVehicleEvent(record, eventMetricsMapping);
                            })));
                        }

                        // If backend return no records or we already reached the end of the records then break
                        if (!cachedEventData.records || cachedEventData.records.length === 0 || (pageNumber + 1) * FETCH_EVENTS_PAGE_LIMIT >= cachedEventData.total) {
                            break;
                        }
                    } else {
                        // If we can't find the data from cache, it is probably not fetched. Add it to the list of data we need to fetch.
                        newParamsForFetchingMissingData.push({
                            homeId,
                            logResourcePath: eventMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                            filters,
                        });
                        break;
                    }
                    pageNumber += 1;
                }

                allTripsEventLogRecordsResult.push(oneTripsEventLogRecords);
            });


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (
                    !currentParams
                    || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });

            // If we don't have any missing data to fetch then we can return the result
            if (newParamsForFetchingMissingData.length === 0) {
                return allTripsEventLogRecordsResult;
            }
        }

        return undefined;
    }, [homeId, eventMetricsDefinition, tripsInfo, entities, eventDataSettings?.metricsNameMapping?.eventKey,
        eventDataSettings?.metricsNameMapping?.eventType, eventDataSettings?.metricsNameMapping?.lat, eventDataSettings?.metricsNameMapping?.lon,
        eventDataSettings?.metricsNameMapping?.speed, appDataState]);



    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchEventLogs(action.homeId, action.logResourcePath, action.filters)).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData]);



    return allTripsEventLogRecords;
};



// Temporary Interface for storing params required for fetching event log data
interface FetchMissingVideoDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly filters?: UIFetchHomeVideosFilters;
}



/**
 * Fetch video data for all events.
 * Given an list of events for all the trips, this function will make API call to fetch videos for EACH event and then add the videos to the event
 * object and return the NEW events objects with videos data in them.
 * Given:
 *      allTripsVehicleEvents: [
 *          [{timestamp, eventType, lat, lng, speed, ...}, {timestamp, eventType, lat, lng, speed, ...}],
 *          [{timestamp, eventType, lat, lng, speed, ...}]
 *      ]
 * Return
 *      [
 *          [{timestamp, eventType, lat, lng, speed, videos, ...}, {timestamp, eventType, lat, lng, speed, videos, ...}],
 *          [{timestamp, eventType, lat, lng, speed, videos, ...}]
 *      ]
 *
 */
export const useFetchMultipleTripsVideosData = (
    projectId: number,
    homeId: number | undefined,
    eventVideoLinkToInfo: EntityMetricsDefinitionLinkTo | undefined,
    videoBlobDefinition: EntityBlobDefinition | undefined,
    allTripsVehicleEvents: readonly (RouteVehicleEvent[] | undefined)[] | undefined,
): readonly (RouteVehicleEvent[] | undefined)[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly FetchMissingVideoDataParams[] | undefined>();
    const hasErrors = useRef<boolean>(false);


    /**
     * Get the videos data from cache. If a videos data is not fetched, add the params needed to fetch for videos to the
     * paramsForFetchingMissingData so that we can go through that list and fetch the missing data later
     */
    const videosBySearchKeyMap = useMemo((): Map<string, readonly VideoInfo[]> | undefined => {

        if (!hasErrors.current && homeId && videoBlobDefinition && eventVideoLinkToInfo && allTripsVehicleEvents) {

            // For optimization, we don't need to make 1 API call to fetch video per 1 event. We can make 1 call for multiple events. The videos
            // for each event is store under it's searchKey and we can pass multiple searchKeys to the API to fetch videos for multiple events.
            // HOWEVER, back end only support up to 20 searchKeys per API call therefore, we will need to group these searchKeys in groups of 20.
            // This is why "allSearchKeysGroups" is a DOUBLE ARRAY but not just an array of searchKeys
            const allSearchKeysGroups: string[][] = [];

            // event.fields contains many fields/values and one of these values is the searchKey for the video associated to the event.
            // The videoLinkToInfo.keyField tells us which field that we need to use.
            const keyField = eventVideoLinkToInfo.keyField;

            allTripsVehicleEvents.forEach((oneTripEvents) => {
                if (oneTripEvents) {
                    oneTripEvents.forEach((event) => {
                        const videoSearchKey = event.fields[keyField];

                        // Get the last searchKeyGroup. If it has less than 20 items the continue adding new videoSearchKey to the group
                        const lastSearchKeyGroup = allSearchKeysGroups.at(-1);
                        if (lastSearchKeyGroup && lastSearchKeyGroup.length < MAX_SEARCH_KEYS) {
                            lastSearchKeyGroup.push(videoSearchKey);
                        } else {
                            // If the last searchKeyGroup does not exist or already has 20 items then create a new group and add the videoSearchKey
                            allSearchKeysGroups.push([videoSearchKey]);
                        }
                    });
                }
            });

            // If there is no search key then it means there is no event therefore we don't need to do anything.
            if (allSearchKeysGroups.length === 0) {
                return undefined;
            }


            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: FetchMissingVideoDataParams [] = [];


            // All the video we fetched will be stored here as a map with the searchKey as the key
            const result: Map<string, VideoInfo[]> = new Map<string, VideoInfo[]>();


            for (const currentSearchKeysGroup of allSearchKeysGroups) {

                let pageNumber = 0;
                while (pageNumber < MAX_PAGES) {

                    const filters: UIFetchHomeVideosFilters = {
                        pageNumber,
                        pageSize  : FETCH_VIDEOS_PAGE_LIMIT,
                        searchKeys: currentSearchKeysGroup.join(','),
                    };

                    // Get the cached videos from the dataState
                    const cachedVideos = selectHomeVideos(
                        appDataState, homeId, videoBlobDefinition.dataStoreConfiguration.moduleInstanceId, filters,
                    );

                    if (cachedVideos) {
                        // Add the videos we fetched to the result map.
                        cachedVideos.forEach((video) => {
                            const searchKey = video.searchKey;

                            // Check the result to see if it already has an Array of videos for the specified searchKey. If it does, add the new
                            // video to the array. If not, create a new array with the new video and also add the new array to the result.
                            const existingVideos = result.get(searchKey);
                            if (existingVideos) {
                                existingVideos.push(video);
                            } else {
                                result.set(searchKey, [video]);
                            }
                        });

                        // if we asked for N videos but got < N videos then we can assume we already at the last page and can stop loading
                        // videos for the currentSearchKeysGroup.
                        if (cachedVideos.length < FETCH_VIDEOS_PAGE_LIMIT) {
                            break;
                        }
                    } else {
                        // If we can't find the data from cache, it is probably not fetched. Add it to the list of data we need to fetch.
                        newParamsForFetchingMissingData.push({
                            homeId,
                            smartModuleId: videoBlobDefinition.dataStoreConfiguration.moduleInstanceId,
                            filters,
                        });
                        break;
                    }

                    pageNumber += 1;
                }

                if (newParamsForFetchingMissingData.length > 0) {
                    break;
                }
            }


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (
                    !currentParams
                    || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });

            // If we don't have any missing data to fetch then we can return the result
            if (newParamsForFetchingMissingData.length === 0) {
                return result;
            }
        }

        return undefined;
    }, [homeId, videoBlobDefinition, eventVideoLinkToInfo, allTripsVehicleEvents, appDataState]);



    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchHomeVideos(action.homeId, action.smartModuleId, action.filters)).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData]);


    /**
     * Once all videos are loaded, go through all the events and add the videos to the event objects
     */
    const allTripsVehicleEventsWithVideos = useMemo(() => {
        if (allTripsVehicleEvents && videosBySearchKeyMap && eventVideoLinkToInfo) {
            return allTripsVehicleEvents?.map((oneTripEvents) => {
                return oneTripEvents?.map((event) => {
                    const keyField = eventVideoLinkToInfo.keyField;
                    const searchKey = event.fields[keyField];

                    if (searchKey) {
                        return {
                            ...event,
                            videos: videosBySearchKeyMap.get(searchKey),
                        };
                    }
                    return event;
                });
            });
        }
        return allTripsVehicleEvents;
    }, [allTripsVehicleEvents, videosBySearchKeyMap, eventVideoLinkToInfo]);


    return allTripsVehicleEventsWithVideos;
};



/**
 * Fetch trip data for multiple trips. This will fetch and return the trips' location and events data.
 */
export const useFetchMultipleTripsData = (
    projectId: number,
    homeId: number | undefined,
    locationMetricsDefinition: EntityMetricsDefinition | undefined,
    eventMetricsDefinition: EntityMetricsDefinition | undefined,
    eventVideoLinkToInfo: EntityMetricsDefinitionLinkTo | undefined,
    videoBlobDefinition: EntityBlobDefinition | undefined,
    customSettings: EntityTripsRouteMapUIComponentCustomSettings | undefined,
    tripsInfo: readonly TripInfo[] | undefined,
): readonly TripData[] | undefined => {

    // Get all the entities involved in the trips
    const entities = useFetchTripsVehicleAndDriverEntities(projectId, tripsInfo);

    const routeUISettings = customSettings?.routeUISettings;

    // Fetch location data for all trips once we have the "locationMetricsDefinition"
    const allTripsRouteLocations = useFetchMultipleTripsLocationData(projectId, homeId, locationMetricsDefinition, tripsInfo, customSettings?.locationDataSettings);

    // Fetch event data for all trips once we have the "eventMetricsDefinition"
    const allTripsEvents = useFetchMultipleTripsEventsData(projectId, homeId, eventMetricsDefinition, tripsInfo, customSettings?.eventDataSettings);

    // Once we loaded the events for the trips, we can load the videos for these events. The videos will be attached to these events objects
    const allTripsEventsWithVideos = useFetchMultipleTripsVideosData(projectId, homeId, eventVideoLinkToInfo, videoBlobDefinition, allTripsEvents);


    /**
     * Once we have loaded locations and events data for all trips, combine the data and return TripData objects for each trip.
     */
    const allTripData = useMemo((): readonly TripData[] | undefined => {

        if (tripsInfo && entities && allTripsRouteLocations && (!eventMetricsDefinition || allTripsEventsWithVideos)) {

            // Get the range of the speed base on all the trips location data. We will use this range data to determine the line color of the route.
            const speedRange = allTripsRouteLocations.reduce((result, locationsData) => {
                if (locationsData) {
                    return getSpeedRange(locationsData, result);
                }
                return result;
            }, {
                min: Number.MAX_SAFE_INTEGER,
                max: Number.MIN_SAFE_INTEGER,
            });


            const result: TripData[] = [];

            tripsInfo.forEach((tripInfo, tripIndex) => {
                const tripLocationsData = allTripsRouteLocations[tripIndex];
                const tripEventRecords = allTripsEventsWithVideos?.[tripIndex];

                const vehicle = entities.find((entity) => {
                    return entity.entityId === tripInfo.vehicleEntityId;
                });

                const driver = entities.find((entity) => {
                    return entity.entityId === tripInfo.driverEntityId;
                });

                if (!vehicle || !tripLocationsData) {
                    return;
                }

                result.push({
                    tripInfo,
                    vehicle,
                    driver,
                    locations: tripLocationsData,

                    // Only break tripLocationsData into segments if "showVehicleSpeed" is set to true.
                    segments: routeUISettings?.showVehicleSpeed
                        ? createSegments(tripLocationsData, speedRange, customSettings?.routeUISettings?.lineColors)
                        : undefined,

                    events: tripEventRecords,
                });
            });

            return result;
        }

        return undefined;
    }, [allTripsEventsWithVideos, allTripsRouteLocations, customSettings?.routeUISettings?.lineColors, entities, eventMetricsDefinition,
        routeUISettings?.showVehicleSpeed, tripsInfo]);



    return allTripData;
};
